import { useState } from "react";

import { useHistory } from "react-router";

import { Button, Link, Text } from "@alpacahq/ui/old";

import { useGetAcatsTransfers } from "~/v2/api/hooks";
import type { AcatsTransfer } from "~/v2/api/rest/acats";
import { FAQ } from "~/v2/components/common/FAQ";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { Layout } from "../../common/Layout";
import { BackButton } from "../BackButton";
import { TERMS_AND_CONDITIONS_URL } from "../const";
import { CannotStartTransferModal } from "./CannotStartTransferModal";
import { OVERVIEW_FAQ } from "./FAQ";
import { PromoBanner } from "./PromoBanner";

const AcatsOverviewPage = () => {
  const history = useHistory();

  const [outgoingInProgressTransfer, setOutgoingInProgressTransfer] =
    useState<AcatsTransfer | null>(null);

  const { currentAccount } = useAccountContext({ liveOnly: true });

  const { data: acats, isLoading: isAcatsLoading } = useGetAcatsTransfers({
    accountId: currentAccount?.id ?? "",
    options: {
      enabled: !!currentAccount?.id,
    },
  });

  const onStartTransferClicked = () => {
    const outgoingInProgressTransfers = acats?.transfers?.filter(
      (transfer) =>
        transfer.direction === "OUTGOING" &&
        transfer.status.transfer_status === "IN_PROGRESS",
    );

    if (outgoingInProgressTransfers?.length) {
      setOutgoingInProgressTransfer(outgoingInProgressTransfers[0]);
      return;
    }

    history.push(Path.ROUTE_PORTFOLIO_TRANSFER_ORIGINATING_BROKER);
  };

  const onBackClicked = () => history.push(Path.ROUTE_FUNDING);

  const isPromoBannerDisplayed = currentAccount?.account_type !== "ira";

  return (
    <div>
      <BackButton
        onClick={onBackClicked}
        label="Back to Transfer Funds"
        className="mb-4"
      />
      {isPromoBannerDisplayed && <PromoBanner />}
      <Layout
        title="Portfolio Transfer"
        faq={<FAQ faq={OVERVIEW_FAQ} />}
        className="mt-2 pt-0"
      >
        <div className="flex flex-col gap-4">
          <section className="flex flex-col gap-2">
            <h2 className="m-0 p-0 text-left font-semibold text-gray-800 text-lg">
              Transfer Fee
            </h2>
            <Text className="text-gray-700">
              Transferring your portfolio in is free. Plus, if you're moving
              $10,000 or more, we'll reimburse up to $50 for any transfer fees
              your current broker may charge.{" "}
              <Link href={TERMS_AND_CONDITIONS_URL}>Get details.</Link>
            </Text>
          </section>
          <section className="flex flex-col gap-2">
            <h2 className="m-0 p-0 text-left font-semibold text-gray-800 text-lg">
              Supported Assets
            </h2>
            <Text className="text-gray-700">
              Alpaca now supports the transfer of{" "}
              <strong>cash, stocks, and ETFs</strong>. Please be advised that
              other asset types are not currently transferable.
            </Text>
          </section>
          <section className="flex flex-col gap-2">
            <h2 className="m-0 p-0 text-left font-semibold text-gray-800 text-lg">
              Processing Time
            </h2>
            <Text className="text-gray-700">
              A Portfolio Transfer could take up to 5 business days.
            </Text>
          </section>
          <Button
            type="button"
            variant="v2/filled"
            className="ml-auto"
            onClick={onStartTransferClicked}
            loading={isAcatsLoading}
          >
            Start Your Transfer
          </Button>
        </div>
      </Layout>
      {outgoingInProgressTransfer && (
        <CannotStartTransferModal
          isOpen={!!outgoingInProgressTransfer}
          onClose={() => setOutgoingInProgressTransfer(null)}
          transfer={outgoingInProgressTransfer}
        />
      )}
    </div>
  );
};

export default AcatsOverviewPage;
